import React from 'react';
import { ApolloProvider } from '@apollo/client';
import './App.scss';
import Loading from './controllers/Animations/loadScreen';
import { Redirect, BrowserRouter as Router, Route, Switch } from 'react-router-dom';
import ToasterNotifications from './controllers/visuals/ToasterNotifications';
import MenuGeneral from './controllers/Menu/Menu';
import Support from './controllers/Support/Support';
import Account from './controllers/Account/Account';
import GraphQlClient from './services/graphql';
import Register from './controllers/Registration/Register';
import Login from './controllers/Registration/Login';
import SetupFiscalPop from './controllers/Setup/Setup';
import OrdersList from './controllers/Orders/Orders.list';
import Facturas from './controllers/Facturas/Facturas';
import FacturaReporte from './controllers/Facturas/Reporte/Facturas.reporte';
import ProductsList from './controllers/Productos/Products.list';
import ItemsBulkEdit from './controllers/Productos/BulkEdit/Items.BulkEdit';
import StampSingle from './controllers/Stamp/Stamp.single';
import StampGlobal from './controllers/StampGlobal/Stamp.global';

import PasswordSet from './controllers/Registration/PasswordSet';
import OrdersReport from './controllers/Orders/Reporte/Reporte';
import OrdersReportList from './controllers/Orders/Reporte/Reporte.list';
import OrdenesReportGeneration from './controllers/Orders/Reporte/generation/Ordenes.reporte.generation';
import ReportProgressNotification from './controllers/visuals/ReportProgress.notification';
import PaymentIntentComplete from './controllers/Account/PaymentIntent.complete';
import CheckoutSuccess from './controllers/Account/Checkout/CheckoutSuccess';
import { hasTitlebarNotice } from './controllers/components/titlebar';
import { CurrentUserProvider, useCurrentUser } from './contexts/CurrentUserContext';

function App() {
  const { currentUser, userFetched } = useCurrentUser();

  const renderLoading = () => {
    return (
      <div id="_AppLoading">
        <Loading display={true} />
      </div>
    )
  }

  const renderForRedirect = (RenderComponent: JSX.Element) => {
    return !currentUser || (!!currentUser && !currentUser.amazonProfile) ? <Redirect to="/login" /> : RenderComponent;
  }

  const renderMenuForLoggedIn = (RenderComponent: JSX.Element) => {
    return !!currentUser && currentUser.amazonProfile ? RenderComponent : '';
  }

  const renderMain = () => {
    return (
      <>
        {
          // Subscription should ONLY run when user exists or is logged in, otherwise the WS auth will bug out.
          currentUser?.amazonProfile ?
            <ToasterNotifications />
            : null
        }
        {
          currentUser?.amazonProfile ?
            <ReportProgressNotification currentUser={currentUser} />
            : null
        }
        <div id="Master" className={`${hasTitlebarNotice(currentUser) ? 'withMessage' : ''} ${!!currentUser && currentUser.amazonProfile ? '' : 'notLogged'}`}>
          <Route path="/" render={(props) => renderMenuForLoggedIn(<MenuGeneral {...props} currentUser={currentUser} />)} />
          <Switch>
            <Route exact path="/" render={(props) => renderForRedirect(
              !currentUser || (!!currentUser && !currentUser.fiscalpopProfile) ? <Redirect to="/setup" {...props} /> : <Redirect to="/ordenes" {...props} />
            )} />

            <Route exact path="/facturas" render={(props) => renderForRedirect(
              !currentUser || (!!currentUser && !currentUser.fiscalpopProfile) ? <Redirect to="/setup" {...props} /> : <Facturas {...props} currentUser={currentUser} />
            )} />
            <Route path="/facturas/report" render={(props) => renderForRedirect(
              !currentUser || (!!currentUser && !currentUser.fiscalpopProfile) ? <Redirect to="/setup" {...props} /> : <FacturaReporte {...props} currentUser={currentUser} />
            )} />
            <Route exact path="/items" render={(props) => renderForRedirect(
              !currentUser || (!!currentUser && !currentUser.fiscalpopProfile) ? <Redirect to="/setup" {...props} /> : <ProductsList {...props} currentUser={currentUser} />
            )} />
            <Route exact path="/items/bulk" render={(props) => renderForRedirect(
              !currentUser || (!!currentUser && !currentUser.fiscalpopProfile) ? <Redirect to="/setup" {...props} /> : <ItemsBulkEdit {...props} currentUser={currentUser} />
            )} />
            <Route exact path="/ordenes" render={(props) => renderForRedirect(
              !currentUser || (!!currentUser && !currentUser.fiscalpopProfile) ? <Redirect to="/setup" {...props} /> : <OrdersList {...props} currentUser={currentUser} />
            )} />
            <Route exact path="/ordenes/report" render={(props) => renderForRedirect(
              !currentUser || (!!currentUser && !currentUser.fiscalpopProfile) ? <Redirect to="/setup" {...props} /> : <OrdersReportList {...props} currentUser={currentUser} />
            )} />
            <Route exact path="/ordenes/report/generate" render={(props) => renderForRedirect(
              !currentUser || (!!currentUser && !currentUser.fiscalpopProfile) ? <Redirect to="/setup" {...props} /> : <OrdersReport {...props} currentUser={currentUser} />
            )} />
            <Route exact path="/ordenes/report/generation/:reportid" render={(props) => renderForRedirect(
              <OrdenesReportGeneration {...props} currentUser={currentUser} />
            )} />
            <Route path="/ordenes/stamp" render={(props) => renderForRedirect(
              !currentUser || (!!currentUser && !currentUser.fiscalpopProfile) ? <Redirect to="/setup" {...props} /> : <StampSingle {...props} currentUser={currentUser} />
            )} />
            <Route path="/ordenes/globalstamp" render={(props) => renderForRedirect(
              !currentUser || (!!currentUser && !currentUser.fiscalpopProfile) ? <Redirect to="/setup" {...props} /> : <StampGlobal {...props} currentUser={currentUser} />
            )} />
            <Route path="/setup" render={(props) => renderForRedirect(
              <SetupFiscalPop {...props} currentUser={currentUser} />
            )} />
            <Route path="/account" render={(props) => renderForRedirect(
              <Account {...props} currentUser={currentUser} />
            )} />
            <Route path="/payment/confirm" render={(props) => renderForRedirect(
              <PaymentIntentComplete {...props} currentUser={currentUser} />
            )} />
            <Route path="/checkout/success" render={(props) => renderForRedirect(
              <CheckoutSuccess {...props} currentUser={currentUser} />
            )} />
            <Route path="/support" render={(props) => renderForRedirect(
              <Support {...props} currentUser={currentUser} />
            )} />
            <Route path="*" render={(props) => <Redirect to="/" {...props} />} />
          </Switch>
        </div>
      </>
    )
  }

  return userFetched ? renderMain() : renderLoading();
}

export default function AppContext() {
  return (
    <ApolloProvider client={GraphQlClient.client}>
      <Router>
        <Switch>
          <Route exact path="/login" component={Login} />
          <Route exact path="/register" component={Register} />
          <Route path="/token/:token" component={PasswordSet} />
          <Route path="*" render={() => (
            <CurrentUserProvider>
              <App />
            </CurrentUserProvider>
          )} />
        </Switch>
      </Router>
    </ApolloProvider>
  );
}
